import React, { useState, useEffect } from 'react';

/**
 * GmailScreenshotUpload
 * ---------------------
 * Self-contained component that renders:
 *   • A red "Upload Gmail Screenshot" button  (no existing record)
 *   • A green "📸 Gmail Screenshot Uploaded" button (existing record)
 *   • Upload modal (file pick → preview → confirm)
 *   • View-existing modal
 *
 * Props:
 *   leadId       – string | number  (required)
 *   leadName     – string           (required, shown in modals)
 *   selectedDate – string YYYY-MM-DD (required)
 */
function GmailScreenshotUpload({ leadId, leadName, selectedDate }) {
    // ── state ────────────────────────────────────────────────────────────────
    const [screenshotRecord, setScreenshotRecord] = useState(null); // null or backend record

    // Upload modal
    const [uploadModal, setUploadModal] = useState(false);
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [confirmStep, setConfirmStep] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    // View modal
    const [viewModal, setViewModal] = useState(false);

    // Toast
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    // ── helpers ───────────────────────────────────────────────────────────────
    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };

    // ── fetch existing record ─────────────────────────────────────────────────
    const fetchStatus = async () => {
        if (!leadId || !selectedDate) return;
        try {
            const res = await fetch(
                `/api/admin/gmail-screenshot-status/?lead_id=${leadId}&tasks_date=${selectedDate}`
            );
            if (res.ok) {
                const data = await res.json();
                setScreenshotRecord(data.exists ? data : null);
            }
        } catch (_) { /* non-critical */ }
    };

    useEffect(() => {
        fetchStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadId, selectedDate]);

    // ── modal open / close ────────────────────────────────────────────────────
    const openUpload = () => {
        setFile(null);
        setPreviewUrl(null);
        setConfirmStep(false);
        setError('');
        setUploadModal(true);
    };

    const closeUpload = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(null);
        setPreviewUrl(null);
        setConfirmStep(false);
        setError('');
        setUploadModal(false);
    };

    // ── file selection ────────────────────────────────────────────────────────
    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];
        const ext = f.name.split('.').pop().toLowerCase();
        const allowedExts = ['png', 'jpg', 'jpeg', 'webp', 'pdf'];
        if (!allowedTypes.includes(f.type) && !allowedExts.includes(ext)) {
            setError('Invalid file type. Allowed: PNG, JPG, JPEG, WEBP, PDF');
            return;
        }
        if (f.size > 5 * 1024 * 1024) {
            setError('File size exceeds 5 MB limit');
            return;
        }
        setError('');
        setFile(f);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(f));
    };

    // ── submit upload ─────────────────────────────────────────────────────────
    const submitUpload = async () => {
        if (!file) return;
        setUploading(true);
        setError('');
        try {
            const csrf = document.cookie.split('; ').find(r => r.startsWith('csrftoken='))?.split('=')[1];
            const formData = new FormData();
            formData.append('lead_id', leadId);
            formData.append('tasks_date', selectedDate);
            formData.append('screenshot', file);

            const res = await fetch('/api/admin/upload-gmail-screenshot/', {
                method: 'POST',
                headers: { ...(csrf && { 'X-CSRFToken': csrf }) },
                body: formData,
            });
            const data = await res.json();

            if (res.ok) {
                showToast('Gmail screenshot uploaded successfully!', 'success');
                await fetchStatus();
                closeUpload();
            } else {
                setError(data.error || 'Upload failed. Please try again.');
                setConfirmStep(false);
            }
        } catch (_) {
            setError('Network error. Please try again.');
            setConfirmStep(false);
        } finally {
            setUploading(false);
        }
    };

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <>
            {/* Trigger button */}
            {screenshotRecord ? (
                <button
                    onClick={() => setViewModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 active:scale-95 text-white text-sm font-semibold rounded-lg shadow transition-all duration-150"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    📸 Gmail Screenshot Uploaded
                </button>
            ) : (
                <button
                    onClick={openUpload}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 active:scale-95 text-white text-sm font-semibold rounded-lg shadow transition-all duration-150"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Upload Gmail Screenshot
                </button>
            )}

            {/* ── Upload Modal ──────────────────────────────────────────────── */}
            {uploadModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

                        {/* Header */}
                        <div className="bg-red-600 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-white">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <h3 className="text-lg font-bold">Upload Gmail Screenshot</h3>
                            </div>
                            <button onClick={closeUpload} className="text-white hover:text-red-200 transition-colors">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6">
                            {/* Lead + Date info */}
                            <div className="flex items-center gap-3 mb-5 bg-gray-50 rounded-lg p-3">
                                <div className="bg-red-100 text-red-600 rounded-full p-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-800">{leadName}</p>
                                    <p className="text-xs text-gray-500">For date: <span className="font-medium text-gray-700">{selectedDate}</span></p>
                                </div>
                            </div>

                            {!confirmStep ? (
                                <>
                                    {/* File Upload Zone */}
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Gmail Screenshot or PDF
                                    </label>
                                    <div
                                        className="relative flex justify-center items-center px-6 pt-8 pb-6 border-2 border-dashed rounded-xl transition-colors cursor-pointer"
                                        style={{ borderColor: file ? '#dc2626' : '#d1d5db' }}
                                    >
                                        {file ? (
                                            <div className="text-center">
                                                <p className="text-sm font-semibold text-red-600 truncate max-w-xs">{file.name}</p>
                                                <p className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                                                <p className="text-xs text-blue-500 mt-2">Click to change file</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2 text-center">
                                                <svg className="mx-auto h-10 w-10 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                <p className="text-sm font-medium text-blue-600">Click to upload screenshot</p>
                                                <p className="text-xs text-gray-400">PNG, JPG, JPEG, WEBP, PDF — up to 5 MB</p>
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            accept=".png,.jpg,.jpeg,.webp,.pdf"
                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                            onChange={handleFileChange}
                                        />
                                    </div>

                                    {/* Preview */}
                                    {previewUrl && file && (
                                        <div className="mt-4 rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                                            <p className="text-xs font-semibold text-gray-500 px-3 py-2 border-b">Preview</p>
                                            {file.type === 'application/pdf' || file.name.endsWith('.pdf') ? (
                                                <iframe src={previewUrl} title="PDF Preview" className="w-full" style={{ height: '300px' }} />
                                            ) : (
                                                <img src={previewUrl} alt="Screenshot preview" className="w-full object-contain max-h-64" />
                                            )}
                                        </div>
                                    )}

                                    {/* Error */}
                                    {error && (
                                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                            ⚠️ {error}
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex gap-3 mt-5">
                                        <button
                                            onClick={closeUpload}
                                            className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => { setConfirmStep(true); setError(''); }}
                                            disabled={!file}
                                            className={`flex-1 py-2.5 rounded-lg font-bold text-white transition-all ${file
                                                ? 'bg-red-600 hover:bg-red-700 active:scale-95 shadow'
                                                : 'bg-gray-300 cursor-not-allowed'
                                                }`}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                </>
                            ) : (
                                /* Confirmation Step */
                                <>
                                    <div className="text-center mb-6">
                                        <div className="w-16 h-16 rounded-full bg-yellow-50 text-yellow-500 flex items-center justify-center mx-auto mb-3 text-3xl">?</div>
                                        <h4 className="text-lg font-bold text-gray-800">Are you sure?</h4>
                                        <p className="text-sm text-gray-500 mt-1">
                                            You are about to upload <span className="font-semibold text-gray-700">{file?.name}</span> as the Gmail screenshot for
                                        </p>
                                        <p className="text-sm font-semibold text-red-600 mt-1">{leadName} — {selectedDate}</p>
                                        <p className="text-xs text-orange-600 mt-2 bg-orange-50 rounded px-2 py-1 inline-block">
                                            ⚠️ Only one Gmail screenshot can be uploaded per lead per day.
                                        </p>
                                    </div>

                                    {error && (
                                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                            ⚠️ {error}
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => { setConfirmStep(false); setError(''); }}
                                            disabled={uploading}
                                            className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                                        >
                                            ← Go Back
                                        </button>
                                        <button
                                            onClick={submitUpload}
                                            disabled={uploading}
                                            className={`flex-1 py-2.5 rounded-lg font-bold text-white transition-all shadow ${uploading
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-green-600 hover:bg-green-700 active:scale-95'
                                                }`}
                                        >
                                            {uploading ? 'Uploading...' : 'Yes, Upload'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── View Existing Modal ───────────────────────────────────────── */}
            {viewModal && screenshotRecord && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">

                        {/* Header */}
                        <div className="bg-green-600 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-white">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <h3 className="text-lg font-bold">Gmail Screenshot — {selectedDate}</h3>
                            </div>
                            <button onClick={() => setViewModal(false)} className="text-white hover:text-green-200 transition-colors">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6">
                            {/* Meta info */}
                            <div className="flex items-center gap-4 mb-4 bg-green-50 rounded-lg p-3">
                                <div className="bg-green-100 text-green-600 rounded-full p-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div className="text-sm">
                                    <p className="font-semibold text-gray-800">{leadName}</p>
                                    <p className="text-gray-500">
                                        Uploaded by <span className="font-medium text-gray-700">{screenshotRecord.uploaded_by}</span>
                                        {screenshotRecord.uploaded_at && (
                                            <> on {new Date(screenshotRecord.uploaded_at).toLocaleString()}</>
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* Screenshot / PDF Preview */}
                            <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                                <p className="text-xs font-semibold text-gray-500 px-3 py-2 border-b">Uploaded Screenshot</p>
                                {screenshotRecord.gmail_screenshot_url?.endsWith('.pdf') ? (
                                    <iframe
                                        src={screenshotRecord.gmail_screenshot_url}
                                        title="Gmail Screenshot PDF"
                                        className="w-full"
                                        style={{ height: '450px' }}
                                    />
                                ) : (
                                    <img
                                        src={screenshotRecord.gmail_screenshot_url}
                                        alt="Gmail screenshot"
                                        className="w-full object-contain max-h-96"
                                    />
                                )}
                            </div>

                            {/* Open in new tab */}
                            <div className="mt-4 flex justify-between items-center">
                                <a
                                    href={screenshotRecord.gmail_screenshot_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    Open in new tab
                                </a>
                                <button
                                    onClick={() => setViewModal(false)}
                                    className="px-5 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast.show && (
                <div className="fixed bottom-4 right-4 z-[60] animate-slide-up">
                    <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                        {toast.type === 'success' ? (
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        ) : (
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        )}
                        <span className="font-medium">{toast.message}</span>
                    </div>
                </div>
            )}
        </>
    );
}

export default GmailScreenshotUpload;
